/**
 * Report export utilities: CSV, Excel, PDF (documented report), JSON.
 * Used by Dashboard Reports tab.
 */
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const REPORT_FILENAME_PREFIX = 'reports';
const getTimestamp = () => new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');

function triggerDownload(blob, filename, mimeType) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Export reports data as CSV (multi-section). */
export function exportReportsCSV(reportsData) {
  const lines = [];
  lines.push('SignTada Analytics Report');
  lines.push(`Generated,${new Date().toISOString()}`);
  lines.push('');

  lines.push('Summary');
  lines.push('Metric,Value');
  lines.push(`Avg. time to sign (hours),${reportsData.avg_time_to_sign_hours != null ? reportsData.avg_time_to_sign_hours : '—'}`);
  lines.push(`Decline rate (%),${reportsData.decline_rate_percent != null ? reportsData.decline_rate_percent : '—'}`);
  lines.push('');

  lines.push('Signing velocity (signed per day)');
  lines.push('Date,Count');
  (reportsData.signing_velocity || []).forEach((r) => lines.push(`${r.date},${r.count}`));
  lines.push('');

  lines.push('Templates usage');
  lines.push('Type,Count');
  (reportsData.templates_breakdown || []).forEach((r) => lines.push(`${r.name},${r.value}`));
  lines.push('');

  lines.push('Monthly volume');
  lines.push('Month,Documents created,Credits used');
  (reportsData.monthly_volume || []).forEach((r) =>
    lines.push(`${r.month},${r.documents_created},${r.credits_used}`)
  );

  const csv = lines.join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, `${REPORT_FILENAME_PREFIX}_${getTimestamp()}.csv`, 'text/csv');
}

/** Export reports data as Excel (multi-sheet). */
export function exportReportsExcel(reportsData) {
  const wb = XLSX.utils.book_new();

  const summary = [
    ['Metric', 'Value'],
    ['Avg. time to sign (hours)', reportsData.avg_time_to_sign_hours != null ? reportsData.avg_time_to_sign_hours : '—'],
    ['Decline rate (%)', reportsData.decline_rate_percent != null ? reportsData.decline_rate_percent : '—'],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');

  const velocity = [['Date', 'Signed count'], ...(reportsData.signing_velocity || []).map((r) => [r.date, r.count])];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(velocity), 'Signing velocity');

  const templates = [['Type', 'Count'], ...(reportsData.templates_breakdown || []).map((r) => [r.name, r.value])];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(templates), 'Templates usage');

  const monthly = [
    ['Month', 'Documents created', 'Credits used'],
    ...(reportsData.monthly_volume || []).map((r) => [r.month, r.documents_created, r.credits_used]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(monthly), 'Monthly volume');

  XLSX.writeFile(wb, `${REPORT_FILENAME_PREFIX}_${getTimestamp()}.xlsx`);
}

/** Professional PDF report (audit-style: header/footer, numbered sections, metadata, clean tables). */
export function exportReportsPDF(reportsData, productName = 'SignTada') {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  const headerH = 56;
  const footerH = 28;

  const generatedAt = new Date();
  const reportId = `RPT-${getTimestamp().slice(0, 8)}`;

  function drawHeader() {
    doc.setFillColor(45, 45, 45);
    doc.rect(0, 0, pageW, headerH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(productName, margin, 22);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Analytics Report', margin, 38);
    doc.setFontSize(10);
    doc.text(`Generated: ${generatedAt.toLocaleString()}`, pageW - margin, 22, { align: 'right' });
    doc.text(`Report ID: ${reportId}`, pageW - margin, 38, { align: 'right' });
  }

  function drawFooter(pageNum, totalPages) {
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    const footerY = pageH - 14;
    doc.text(`${productName} — Confidential`, margin, footerY);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageW / 2, footerY, { align: 'center' });
    doc.text(generatedAt.toLocaleDateString(), pageW - margin, footerY, { align: 'right' });
  }

  const tableTheme = {
    headStyles: { fillColor: [72, 72, 72], textColor: 255, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    margin: { left: margin, right: margin },
    theme: 'striped',
    styles: { cellPadding: 6 },
  };

  drawHeader();
  let y = headerH + 24;

  // --- Report metadata block ---
  doc.setFillColor(250, 250, 250);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.rect(margin, y, pageW - 2 * margin, 32, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text('Report period', margin + 8, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.text('Last 90 days (or selected range)', margin + 8, y + 24);
  doc.setFont('helvetica', 'bold');
  doc.text('Report ID', pageW - margin - 120, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.text(reportId, pageW - margin - 120, y + 24);
  y += 44;

  // --- 1. Executive summary ---
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('1. Executive summary', margin, y);
  y += 20;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const avgHrs = reportsData.avg_time_to_sign_hours != null ? `${reportsData.avg_time_to_sign_hours} hrs` : '—';
  const declinePct = reportsData.decline_rate_percent != null ? `${reportsData.decline_rate_percent}%` : '—';
  doc.text(`Average time to sign (send to completion): ${avgHrs}`, margin + 8, y);
  y += 14;
  doc.text(`Decline rate (rejected / signed + rejected): ${declinePct}`, margin + 8, y);
  y += 28;

  // --- 2. Signing velocity ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('2. Signing velocity (documents signed per day)', margin, y);
  y += 10;
  const velocityData = (reportsData.signing_velocity || []).map((r) => [r.date, String(r.count)]);
  autoTable(doc, {
    ...tableTheme,
    startY: y,
    head: [['Date', 'Signed count']],
    body: velocityData.length ? velocityData : [['No data', '—']],
  });
  y = doc.lastAutoTable.finalY + 22;

  if (y > pageH - footerH - 80) {
    doc.addPage();
    y = margin;
    drawHeader();
    y = headerH + 20;
  }

  // --- 3. Templates usage ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('3. Templates usage', margin, y);
  y += 10;
  const templatesData = (reportsData.templates_breakdown || []).map((r) => [r.name, String(r.value)]);
  autoTable(doc, {
    ...tableTheme,
    startY: y,
    head: [['Type', 'Count']],
    body: templatesData.length ? templatesData : [['No data', '—']],
  });
  y = doc.lastAutoTable.finalY + 22;

  if (y > pageH - footerH - 80) {
    doc.addPage();
    y = margin;
    drawHeader();
    y = headerH + 20;
  }

  // --- 4. Monthly volume ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('4. Monthly volume vs credits used', margin, y);
  y += 10;
  const monthlyData = (reportsData.monthly_volume || []).map((r) => [
    r.month,
    String(r.documents_created),
    String(r.credits_used),
  ]);
  autoTable(doc, {
    ...tableTheme,
    startY: y,
    head: [['Month', 'Documents created', 'Credits used']],
    body: monthlyData.length ? monthlyData : [['No data', '—', '—']],
  });

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    drawFooter(i, pageCount);
  }

  doc.save(`${REPORT_FILENAME_PREFIX}_${getTimestamp()}.pdf`);
}

/** Export reports data as JSON. */
export function exportReportsJSON(reportsData) {
  const json = JSON.stringify(
    {
      generated: new Date().toISOString(),
      summary: {
        avg_time_to_sign_hours: reportsData.avg_time_to_sign_hours,
        decline_rate_percent: reportsData.decline_rate_percent,
      },
      signing_velocity: reportsData.signing_velocity,
      templates_breakdown: reportsData.templates_breakdown,
      monthly_volume: reportsData.monthly_volume,
    },
    null,
    2
  );
  const blob = new Blob([json], { type: 'application/json' });
  triggerDownload(blob, `${REPORT_FILENAME_PREFIX}_${getTimestamp()}.json`, 'application/json');
}
